import { Helmet } from 'react-helmet-async';
import SEO_CONFIG from '@/lib/seo-config';

/**
 * SEO Head Component
 * Renders all meta tags, Open Graph, Twitter Cards, and JSON-LD structured data
 * 
 * @param {Object} props
 * @param {string} props.title - Page title
 * @param {string} props.description - Page description
 * @param {string} props.keywords - Page-specific keywords (comma-separated)
 * @param {string} props.image - OG image URL (optional)
 * @param {string} props.url - Canonical URL (optional, defaults to current page)
 * @param {string} props.type - OG type (website, article, product)
 * @param {Object} props.article - Article metadata for blog posts
 * @param {Object} props.product - Product metadata for pricing pages
 * @param {Array} props.breadcrumbs - Breadcrumb items for structured data
 * @param {Object} props.faq - FAQ items for FAQ schema
 * @param {boolean} props.noindex - Set to true to prevent indexing
 */
export default function SEOHead({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  article,
  product,
  breadcrumbs,
  faq,
  noindex = false,
}) {
  const {
    siteName,
    siteUrl,
    siteDescription,
    defaultKeywords,
    twitterHandle,
    facebookAppId,
    googleSiteVerification,
    bingSiteVerification,
    yandexVerification,
    defaultOgImage,
    locale,
    language,
    organization,
    socialLinks,
  } = SEO_CONFIG;

  // Build full values
  const fullTitle = title ? `${title}` : `${siteName} - Managed OpenClaw AI Hosting`;
  const fullDescription = description || siteDescription;
  const fullKeywords = keywords ? `${keywords}, ${defaultKeywords}` : defaultKeywords;
  const fullImage = image ? `${siteUrl}${image}` : `${siteUrl}${defaultOgImage}`;
  const canonicalUrl = url ? `${siteUrl}${url}` : typeof window !== 'undefined' ? window.location.href : siteUrl;

  // Organization Schema
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: organization.name,
    url: siteUrl,
    logo: `${siteUrl}${organization.logo}`,
    email: organization.email,
    sameAs: socialLinks,
    contactPoint: {
      '@type': 'ContactPoint',
      email: organization.email,
      contactType: 'customer service',
      availableLanguage: ['English'],
    },
  };

  // Website Schema
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteName,
    url: siteUrl,
    description: siteDescription,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${siteUrl}/search?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };

  // Breadcrumb Schema
  const breadcrumbSchema = breadcrumbs ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${siteUrl}${item.url}`,
    })),
  } : null;

  // FAQ Schema
  const faqSchema = faq ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faq.map(item => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  } : null;

  // Product Schema (for pricing pages)
  const productSchema = product ? {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description,
    brand: {
      '@type': 'Brand',
      name: siteName,
    },
    offers: {
      '@type': 'Offer',
      price: product.price,
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
      seller: {
        '@type': 'Organization',
        name: siteName,
      },
    },
  } : null;

  // Article Schema (for blog posts)
  const articleSchema = article ? {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description: description,
    image: fullImage,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: siteName,
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}${organization.logo}`,
      },
    },
    datePublished: article.publishedTime,
    dateModified: article.modifiedTime || article.publishedTime,
  } : null;

  // SoftwareApplication Schema (main product)
  const softwareSchema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'ClawStinger',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    description: 'Managed hosting platform for OpenClaw AI agents. Deploy Claude, GPT, and AI assistants in under 60 seconds.',
    offers: {
      '@type': 'Offer',
      price: '29',
      priceCurrency: 'USD',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.8',
      ratingCount: '156',
    },
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang={language} />
      <title>{fullTitle}</title>
      <meta name="description" content={fullDescription} />
      <meta name="keywords" content={fullKeywords} />
      <meta name="author" content={siteName} />
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Verification Tags */}
      {googleSiteVerification && <meta name="google-site-verification" content={googleSiteVerification} />}
      {bingSiteVerification && <meta name="msvalidate.01" content={bingSiteVerification} />}
      {yandexVerification && <meta name="yandex-verification" content={yandexVerification} />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={fullDescription} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:locale" content={locale} />
      {facebookAppId && <meta property="fb:app_id" content={facebookAppId} />}

      {/* Article specific OG tags */}
      {article && (
        <>
          <meta property="article:published_time" content={article.publishedTime} />
          {article.modifiedTime && <meta property="article:modified_time" content={article.modifiedTime} />}
          <meta property="article:author" content={article.author} />
          {article.section && <meta property="article:section" content={article.section} />}
          {article.tags?.map((tag, i) => <meta key={i} property="article:tag" content={tag} />)}
        </>
      )}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={twitterHandle} />
      <meta name="twitter:creator" content={twitterHandle} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={fullDescription} />
      <meta name="twitter:image" content={fullImage} />

      {/* Additional SEO Meta Tags */}
      <meta name="theme-color" content="#050505" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="format-detection" content="telephone=no" />
    </Helmet>
  );
}

/**
 * StructuredData Component
 * Renders JSON-LD structured data directly in the DOM
 * Use this alongside SEOHead for proper schema.org markup
 */
export function StructuredData({ data }) {
  if (!data) return null;
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}
